import { zodResolver } from '@hookform/resolvers/zod';
import { useLayoutEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';

import { FormDrawer } from '@/components/feedback/drawer';
import { useToast } from '@/components/feedback/toast';
import { PhoneField, TextField } from '@/components/forms';
import { ApiError } from '@/lib/api/errors';

import { DEALER_ERROR_CODES, DEALER_GENERIC_ERROR } from '../constants';
import { buildDealerFormSchema, COMPANY_NAME_DISALLOWED, DEALER_FORM_FIELDS } from '../schemas';
import type { DealerFormOutput, DealerFormValues } from '../types';

import { CitySelectField } from './city-select-field';

const EMPTY: DealerFormValues = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  companyName: '',
  city: null,
};

const CONFLICTS: Partial<Record<string, [keyof DealerFormValues, string]>> = {
  [DEALER_ERROR_CODES.phoneExists]: ['phone', 'This phone number already exists.'],
  [DEALER_ERROR_CODES.emailExists]: ['email', 'This email address already exists.'],
  [DEALER_ERROR_CODES.companyNameExists]: ['companyName', 'This Company Name already exists.'],
};

/** One flattened class-validator failure (`{ field, message }[]` in the envelope's `details`). */
type ValidationDetail = { field: string; message: string };
/** BE validates `city` and `state` separately; both are the one City input here. */
const toFormField = (field: string) => (field === 'state' ? 'city' : field);
const isFormField = (field: string): field is keyof DealerFormValues =>
  DEALER_FORM_FIELDS.includes(field);

export type DealerFormDrawerProps = {
  open: boolean;
  mode?: 'create' | 'edit';
  /** Edit's prefill; re-applied on every closed→open transition. */
  initialValues?: DealerFormValues;
  onSubmit: (values: DealerFormOutput) => Promise<void>;
  onClose: () => void;
};

/** Create and Edit Dealer (118643), modelled on `admin-user-form-drawer.tsx` (grilling Q14). A
 *  drawer, not the AC's centred modal (grilling Q5/F6). */
export function DealerFormDrawer({
  open,
  mode = 'create',
  initialValues,
  onSubmit,
  onClose,
}: DealerFormDrawerProps) {
  const toast = useToast();
  // Edit: an unchanged stored phone that predates the US rule must not block Apply (ADR-0035).
  const storedPhone = mode === 'edit' ? initialValues?.phone : undefined;
  const schema = useMemo(() => buildDealerFormSchema(storedPhone), [storedPhone]);
  const form = useForm<DealerFormValues, unknown, DealerFormOutput>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: initialValues ?? EMPTY,
  });

  // Re-seed only on closed→open, never on `initialValues` identity: a mid-submit re-render would
  // otherwise revert the admin's edits (admin-user-form-drawer.tsx). A seeded form is validated at
  // once so Apply reads `isValid` without waiting for a touch (AC: enabled once all six are valid).
  const wasOpen = useRef(false);
  useLayoutEffect(() => {
    if (open && !wasOpen.current) {
      form.reset(initialValues ?? EMPTY);
      if (initialValues) void form.trigger();
    }
    wasOpen.current = open;
  }, [open, initialValues, form]);

  const handleSubmit = async (values: DealerFormOutput) => {
    try {
      await onSubmit(values);
      // This path bypasses FormDrawer's own close (and its reset), and the drawer stays mounted,
      // so without this the next "+ Add Dealer" would open on the Dealer just created.
      form.reset();
      onClose();
    } catch (error) {
      if (error instanceof ApiError) {
        const conflict = error.code ? CONFLICTS[error.code] : undefined;
        if (conflict) return form.setError(conflict[0], { message: conflict[1] });
        // The BE's own copy, "Select a city from the list." — the AC words none.
        if (error.code === DEALER_ERROR_CODES.invalidCity)
          return form.setError('city', { message: error.message });
        if (error.code === DEALER_ERROR_CODES.validationFailed) {
          const details = (Array.isArray(error.details) ? error.details : []) as ValidationDetail[];
          let mapped = false;
          for (const detail of details) {
            const field = toFormField(detail.field);
            if (!isFormField(field)) continue;
            form.setError(field, { message: detail.message });
            mapped = true;
          }
          if (mapped) return;
        }
      }
      toast.error({ title: DEALER_GENERIC_ERROR });
    }
  };

  return (
    <FormDrawer
      form={form}
      open={open}
      title={mode === 'edit' ? 'Edit Dealer' : 'Create Dealer'}
      // AC "Apply" (Figma 1913:843831 says "Save" — AC wins content, grilling Q4).
      submitText={mode === 'edit' ? 'Apply' : 'Create'}
      primaryDisabled={!form.formState.isValid}
      // AC: an overlay click closes silently even with values typed — override the dirty guard.
      maskClosable
      onClose={onClose}
      onSubmit={handleSubmit}
    >
      <TextField
        name="firstName"
        control={form.control}
        label="First Name"
        placeholder="Enter first name"
        maxLength={50}
        required
      />
      <TextField
        name="lastName"
        control={form.control}
        label="Last Name"
        placeholder="Enter last name"
        maxLength={50}
        required
      />
      <PhoneField
        name="phone"
        control={form.control}
        label="Phone Number"
        placeholder="Enter Phone No"
        required
      />
      <TextField
        name="email"
        control={form.control}
        label="Email"
        placeholder="Enter email"
        maxLength={100}
        required
      />
      <TextField
        name="companyName"
        control={form.control}
        label="Company Name"
        placeholder="Enter company name"
        maxLength={250}
        transform={(raw) => raw.replace(COMPANY_NAME_DISALLOWED, '')}
        required
      />
      <CitySelectField control={form.control} />
    </FormDrawer>
  );
}
